'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the row widens by the card's p-3 on both sides and no token carries that */

import classNames from 'classnames';
import { useEffect, useId, useRef } from 'react';
import { MaterialIcon } from '../../components/MaterialIcon';
import type { CmsProduct } from '../../types/cmsProduct';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import {
  useCompareListingEnabled,
  useCompareListingIndex,
} from './CompareListing';
import { useCompare } from './CompareProvider';
import { compareIdOf } from './compareUrl';
import {
  COMPARE_VARIANT_LAYOUT,
  compareVariantCopy,
  compareVariantOf,
  type CompareVariantLayout,
} from './compareVariant';
import { TRANSLATIONS } from './i18n';
import { trackCardCheckbox } from './tracking';

export type CompareToggleProps = {
  product: CmsProduct | undefined;
  /** The card's position in its list, which the compare event reports. */
  index?: number | string;
  className?: string;
};

/**
 * What every compare control on a card needs: the product registered with the provider, whether it
 * is selected, whether the cap disables it, and the toggle itself. `null` without a provider or a
 * comparable product, and for a control the vertical does not use (the TI row on a credit card),
 * so the control renders nothing.
 */
export function useCompareToggle(
  product: CmsProduct | undefined,
  control: CompareVariantLayout['cardControl'],
  /** The card's position in its list, which the compare event reports. */
  index?: number | string,
) {
  const compare = useCompare();
  const context = useCmsClientContext();
  const id = compareIdOf(product);
  const variant = compare ? compareVariantOf(compare.vertical) : undefined;
  const ownControl =
    !!variant && COMPARE_VARIANT_LAYOUT[variant].cardControl === control;

  // The tray shows a logo and a plan name for every selected id. A selection restored from a
  // shared link or from sessionStorage was never clicked, so the cards make themselves known here.
  const listingEnabled = useCompareListingEnabled();
  const listingIndex = useCompareListingIndex();
  const register = compare?.register;
  // Keyed by the product's id, not the object: a listing re-renders with fresh objects on every
  // quote update. Only a product the control accepts (one with a catalogue uid) is registered.
  const latest = useRef(product);
  latest.current = product;
  // Names the cap hint, so a disabled control describes itself to a screen reader and not only to a
  // hovering pointer.
  const hintId = useId();
  const comparable =
    ownControl && listingEnabled && product?.uid ? id : undefined;
  useEffect(() => {
    if (comparable) register?.(latest.current);
  }, [register, comparable]);

  // Only a product with a catalogue uid can be compared: the comparison page loads plans by it.
  if (
    !compare ||
    !variant ||
    !ownControl ||
    !id ||
    !product?.uid ||
    !listingEnabled
  )
    return null;

  const staticText = getClientTranslations(TRANSLATIONS, context);
  const selected = compare.isSelected(id);
  const disabled = !selected && compare.isFull;
  const copy = compareVariantCopy(variant, staticText);
  return {
    id,
    selected,
    disabled,
    staticText,
    capHint: copy.atCap(compare.cap),
    hintId,
    label: tr('compare_toggle_label', staticText, {
      name: product.product_name ?? id,
    }),
    toggle: () => {
      if (disabled || !product) return;
      // From what the provider did, not from this render: two cards clicked in one tick both see
      // room below the cap, and only the first is let in.
      const outcome = compare.toggle(product);
      if (outcome && compare.tracking)
        trackCardCheckbox(compare.tracking, {
          componentIndex: listingIndex,
          checked: outcome === 'added',
          plan: { product, index: index ?? 0 },
        });
    },
  };
}

/**
 * The "Compare" footer row of a product card. The whole row is the control (a checkbox by role).
 * Renders nothing without a `CompareProvider`, or where the vertical compares with the pill, so it
 * can sit in the shared card and only appear on pages that compare plans. At the cap an unselected
 * row is disabled and greys out.
 */
export const CompareToggle = ({
  product,
  index,
  className,
}: CompareToggleProps) => {
  const state = useCompareToggle(product, 'row', index);
  if (!state) return null;
  const { id, selected, disabled, staticText, capHint, hintId, label, toggle } =
    state;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-describedby={disabled ? hintId : undefined}
      title={disabled ? capHint : undefined}
      data-component="compare-toggle"
      data-product-id={id}
      data-selected={selected}
      onClick={toggle}
      className={classNames(
        // -mx-3/-mb-3 cancel the card's p-3: the divider runs edge to edge and the row closes the card.
        '-mx-3 -mb-3 mt-3 block w-[calc(100%+1.5rem)] border-t border-borders/default px-spacing/gap/soft-12px text-left',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      <span className="flex w-full items-center justify-between px-spacing/padding/soft-12px py-spacing/padding/y-sm">
        <span
          className={classNames(
            'mhg-alpha-btn-small-text',
            disabled ? 'text-text/disabled' : 'text-text/primary',
          )}
        >
          {tr('compare', staticText)}
        </span>
        <span
          aria-hidden="true"
          className={classNames(
            // The checkbox corner is a fixed 4px in every breakpoint, unlike radius/tooltip.
            'flex size-5 shrink-0 items-center justify-center rounded border',
            selected
              ? 'border-buttons/primary bg-buttons/primary text-buttons/base'
              : 'bg-buttons/base',
            !selected &&
              (disabled
                ? 'border-borders/default'
                : 'border-borders/form-default'),
          )}
        >
          {selected && <MaterialIcon icon="check" px={16} />}
        </span>
      </span>
      {disabled && (
        <span id={hintId} className="sr-only">
          {capHint}
        </span>
      )}
    </button>
  );
};
